/* eslint-disable no-restricted-syntax */
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMixpanel } from 'react-mixpanel-browser';

import { State } from 'redux/initialState';
import { store } from 'redux/store';
import { useAuth } from 'hooks/useAuth';
import { useAiPanelLinksEnabled } from 'modules/ai/hooks/useAiPanelLinksEnabled';
import {
  AI_EVENTS,
  AiAnalyticsContext,
} from 'modules/ai/constants/aiAnalyticsEvents';
import { createMessageId } from 'modules/ai/pages/SocietyAiAgent/utils/createMessageId';
import trackAiActivity from 'modules/ai/utils/trackAiActivity';
import {
  appendAiMessage,
  getAiUsage,
  setAiConversationId,
  setAiStreaming,
} from 'modules/ai/redux/aiActions';

type ChatStreamOptions = {
  societyId: string;
  conversationId: string | null;
  message: string;
  enablePanelLinks?: boolean;
  userMessageId?: string;
  assistantMessageId?: string;
  analyticsContext?: AiAnalyticsContext;
  conversationTurn?: number;
  isFirstMessage?: boolean;
  onConversationId?: (id: string) => void; // eslint-disable-line no-unused-vars
  onFirstChunk?: () => void; // eslint-disable-line no-unused-vars
  onDone?: () => void;
  persistOnUnmount?: boolean;
};

type SseDonePayload = {
  messageId?: string;
  outcome?: string;
  routerType?: string;
  entity?: string | null;
  resultCount?: number | null;
  durationMs?: number | null;
  panelLinksCount?: number;
};

type SseEvent = { event: string; data: string };

const parseSseBlock = (block: string): SseEvent | null => {
  const lines = block.split('\n');
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (!dataLines.length) return null;
  return { event, data: dataLines.join('\n') };
};

export const useAiChatStream = () => {
  const dispatch = useDispatch();
  const mixpanel = useMixpanel();
  const { getAccessTokenSilently } = useAuth();
  const panelLinksEnabled = useAiPanelLinksEnabled();

  const isStreaming = useSelector(
    (state: State) => state.ai?.isStreaming ?? false
  );
  const streamingText = useSelector(
    (state: State) => state.ai?.streamingText ?? ''
  );
  const errorRef = useRef<string | null>(null);
  const error = errorRef.current;

  const abortRef = useRef<AbortController | null>(null);
  const persistOnUnmountRef = useRef(false);

  useEffect(
    () => () => {
      if (persistOnUnmountRef.current) {
        persistOnUnmountRef.current = false;
        return;
      }
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    },
    []
  );

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    dispatch(setAiStreaming({ isStreaming: false, streamingText: '' }));
  }, [dispatch]);

  const sendMessage = useCallback(
    async ({
      societyId,
      conversationId,
      message,
      enablePanelLinks = panelLinksEnabled,
      userMessageId: userMessageIdParam,
      assistantMessageId: assistantMessageIdParam,
      analyticsContext,
      conversationTurn,
      isFirstMessage,
      onConversationId,
      onFirstChunk,
      onDone,
      persistOnUnmount = false,
    }: ChatStreamOptions) => {
      errorRef.current = null;
      persistOnUnmountRef.current = persistOnUnmount;
      dispatch(setAiStreaming({ isStreaming: true, streamingText: '' }));

      const submitStartedAt = performance.now();
      const userMessageId = userMessageIdParam || createMessageId('u');
      const assistantMessageId =
        assistantMessageIdParam || createMessageId('a');
      let firstChunkAt: number | null = null;
      let activeConversationId = conversationId;

      const track = (
        eventName: (typeof AI_EVENTS)[keyof typeof AI_EVENTS],
        extras: Record<string, unknown> = {}
      ) => {
        trackAiActivity({
          mixpanel,
          state: store.getState() as State,
          eventName,
          extras: {
            conversationId: activeConversationId,
            messageId: assistantMessageId,
            userMessageId,
            ...extras,
          },
        });
      };

      track(AI_EVENTS.MESSAGE_SENT, {
        entryPoint: analyticsContext?.entryPoint ?? 'agent_page',
        embedContext: analyticsContext?.embedContext,
        isSuggestion: analyticsContext?.isSuggestion === true,
        suggestionKey: analyticsContext?.suggestionKey,
        conversationTurn,
        isFirstMessage,
        questionLength: message.trim().length,
        question: message.trim(),
      });

      const controller = new AbortController();
      abortRef.current = controller;
      let hasCalledFirstChunk = false;

      try {
        const token = (await getAccessTokenSilently()) as unknown as
          | string
          | null;
        const baseUrl = process.env.REACT_APP_API_URL_V2 || '';

        const response = await fetch(`${baseUrl}/ai/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            Authorization: token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({
            societyId,
            conversationId,
            message,
            enablePanelLinks: enablePanelLinks === true,
            userMessageId,
            assistantMessageId,
            analyticsContext,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          if (response.status === 402) {
            const body = await response.json().catch(() => ({}));
            errorRef.current = 'AI_QUOTA_EXCEEDED';
            dispatch(setAiStreaming({ isStreaming: false, streamingText: '' }));
            track(AI_EVENTS.QUOTA_EXCEEDED, {
              used: body?.used,
              limit: body?.limit,
              tier: body?.tier,
              entryPoint: analyticsContext?.entryPoint ?? 'agent_page',
              latencyMs: Math.round(performance.now() - submitStartedAt),
            });
            throw Object.assign(new Error('AI quota exceeded'), {
              code: 'AI_QUOTA_EXCEEDED',
              ...body,
            });
          }
          errorRef.current = `HTTP ${response.status}`;
          dispatch(setAiStreaming({ isStreaming: false, streamingText: '' }));
          track(AI_EVENTS.RESPONSE_ERROR, {
            errorCode: `HTTP_${response.status}`,
            errorMessage: `HTTP ${response.status}`,
            latencyMs: Math.round(performance.now() - submitStartedAt),
            recoverable: false,
          });
          throw new Error(`HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error('Response has no body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let accumulated = '';

        const dispatchFinalUsage = () => {
          dispatch(getAiUsage(societyId) as any);
        };

        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          let sep = buffer.indexOf('\n\n');
          while (sep !== -1) {
            const rawBlock = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            const ev = parseSseBlock(rawBlock);
            if (ev) {
              try {
                const payload = JSON.parse(ev.data);
                if (ev.event === 'start' && payload.conversationId) {
                  activeConversationId = payload.conversationId;
                  dispatch(setAiConversationId(payload.conversationId));
                  onConversationId?.(payload.conversationId);
                  track(AI_EVENTS.CONVERSATION_STARTED, {
                    conversationId: payload.conversationId,
                    latencyMs: Math.round(performance.now() - submitStartedAt),
                  });
                } else if (ev.event === 'chunk' && payload.content) {
                  accumulated += payload.content;
                  dispatch(
                    setAiStreaming({
                      isStreaming: true,
                      streamingText: accumulated,
                    })
                  );
                  if (!hasCalledFirstChunk) {
                    hasCalledFirstChunk = true;
                    firstChunkAt = performance.now();
                    track(AI_EVENTS.FIRST_TOKEN_RECEIVED, {
                      firstTokenLatencyMs: Math.round(
                        firstChunkAt - submitStartedAt
                      ),
                    });
                    onFirstChunk?.();
                  }
                } else if (ev.event === 'done') {
                  const donePayload = payload as SseDonePayload;
                  const doneAt = performance.now();
                  dispatch(
                    appendAiMessage({
                      id: donePayload.messageId || assistantMessageId,
                      role: 'assistant',
                      content: accumulated,
                      createdAt: new Date().toISOString(),
                    })
                  );
                  dispatch(
                    setAiStreaming({ isStreaming: false, streamingText: '' })
                  );
                  dispatchFinalUsage();
                  track(AI_EVENTS.RESPONSE_COMPLETED, {
                    responseLength: accumulated.length,
                    totalLatencyMs: Math.round(doneAt - submitStartedAt),
                    streamingDurationMs:
                      firstChunkAt != null
                        ? Math.round(doneAt - firstChunkAt)
                        : null,
                    panelLinksCount: donePayload.panelLinksCount ?? 0,
                    outcome: donePayload.outcome,
                    routerType: donePayload.routerType,
                    entity: donePayload.entity,
                    resultCount: donePayload.resultCount,
                    serverDurationMs: donePayload.durationMs,
                  });
                  onDone?.();
                } else if (ev.event === 'error') {
                  errorRef.current = payload.message || 'Error';
                  track(AI_EVENTS.RESPONSE_ERROR, {
                    errorCode: 'SSE_ERROR',
                    errorMessage: payload.message || 'Error',
                    latencyMs: Math.round(performance.now() - submitStartedAt),
                    recoverable: false,
                  });
                  dispatch(
                    setAiStreaming({ isStreaming: false, streamingText: '' })
                  );
                }
              } catch {
                // ignore non-JSON payloads
              }
            }
            sep = buffer.indexOf('\n\n');
          }
        }

        dispatch(setAiStreaming({ isStreaming: false, streamingText: '' }));
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          dispatch(setAiStreaming({ isStreaming: false, streamingText: '' }));
          return;
        }
        dispatch(setAiStreaming({ isStreaming: false, streamingText: '' }));
        if (!errorRef.current) {
          errorRef.current = err?.message || 'Streaming failed';
          track(AI_EVENTS.RESPONSE_ERROR, {
            errorCode: err?.code || 'STREAM_FAILED',
            errorMessage: err?.message || 'Streaming failed',
            latencyMs: Math.round(performance.now() - submitStartedAt),
            recoverable: true,
          });
        }
        throw err;
      } finally {
        abortRef.current = null;
        persistOnUnmountRef.current = false;
      }
    },
    [dispatch, getAccessTokenSilently, mixpanel, panelLinksEnabled]
  );

  return {
    sendMessage,
    cancel,
    isStreaming,
    streamingText,
    error,
  };
};
